// src/pages/Casting/components/MatsSidebar.jsx

import React, { useMemo } from "react";
import {
  FaBroadcastTower,
  FaCheckCircle,
  FaListUl,
  FaLock,
  FaPlus,
  FaTable,
  FaTimesCircle,
  FaTrash,
  FaTrophy,
  FaUnlock,
} from "react-icons/fa";

const SURFACE_TYPE_CONFIG = {
  MAT: {
    label: "Tapis",
    icon: FaTable,
  },

  CENTRAL_TABLE: {
    label: "Table centrale",
    icon: FaListUl,
  },

  PODIUM: {
    label: "Podium",
    icon: FaTrophy,
  },

  OTHER: {
    label: "Autre surface",
    icon: FaTable,
  },
};

const getSurfaceSlots = (surface) => {
  if (Array.isArray(surface?.slots)) {
    return surface.slots;
  }

  if (Array.isArray(surface?.Slots)) {
    return surface.Slots;
  }

  return [];
};

const getActiveSlotsCount = (surface) =>
  getSurfaceSlots(surface).filter(
    (slot) =>
      slot?.is_active !== false && Boolean(String(slot?.label || "").trim()),
  ).length;

const isSurfaceOpen = (surface) =>
  surface?.is_open !== false && surface?.is_open !== 0;

export default function MatsSidebar({
  mats = [],
  selectedMat,
  onSelectMat,
  currentLabel,
  isOpen,
  onToggleCurrentMat,
  onAddSurface,
  addingSurface = false,
  onDeleteSurface,
  deletingSurface = false,
}) {
  const sortedMats = useMemo(
    () =>
      [...mats].sort((a, b) => {
        const orderA = Number(a?.sort_order) || Number(a?.mat_number) || 0;

        const orderB = Number(b?.sort_order) || Number(b?.mat_number) || 0;

        return orderA - orderB;
      }),
    [mats],
  );

  const selectedSurface = useMemo(
    () =>
      sortedMats.find(
        (surface) => Number(surface.mat_number) === Number(selectedMat),
      ) || null,
    [sortedMats, selectedMat],
  );

  return (
    <aside className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.08)] dark:border-white/10 dark:bg-slate-900 dark:shadow-[0_20px_60px_rgba(0,0,0,0.35)]">
      <div className="border-b border-slate-200 px-4 py-4 dark:border-white/10">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.2em] text-sky-700 dark:text-sky-300">
              Surfaces du dojo
            </p>

            <h2 className="mt-1 text-lg font-black text-slate-950 dark:text-white">
              {sortedMats.length} surface
              {sortedMats.length > 1 ? "s" : ""}
            </h2>

            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              {sortedMats.filter(isSurfaceOpen).length} ouverte
              {sortedMats.filter(isSurfaceOpen).length > 1 ? "s" : ""}
            </p>
          </div>

          <button
            type="button"
            onClick={onAddSurface}
            disabled={
              addingSurface ||
              typeof onAddSurface !== "function" ||
              sortedMats.length >= 30
            }
            className="inline-flex min-h-10 shrink-0 items-center justify-center gap-2 rounded-xl bg-sky-600 px-3 text-xs font-black text-white shadow-lg shadow-sky-600/20 transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
            title="Ajouter une nouvelle surface"
          >
            <FaPlus />

            {addingSurface ? "Ajout…" : "Ajouter"}
          </button>
        </div>
      </div>

      <div className="max-h-[620px] space-y-2 overflow-y-auto p-3">
        {sortedMats.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-center dark:border-white/15">
            <p className="text-sm font-bold text-slate-700 dark:text-slate-200">
              Aucune surface configurée
            </p>

            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Initialise d’abord le nombre de surfaces du dojo.
            </p>
          </div>
        ) : (
          sortedMats.map((surface) => {
            const surfaceType = surface?.surface_type || "MAT";

            const config =
              SURFACE_TYPE_CONFIG[surfaceType] || SURFACE_TYPE_CONFIG.OTHER;

            const SurfaceIcon = config.icon;

            const open = isSurfaceOpen(surface);

            const isSelected =
              Number(selectedMat) === Number(surface.mat_number);

            const activeSlots = getActiveSlotsCount(surface);

            const hasStream = Boolean(
              String(surface?.stream?.stream_url || "").trim(),
            );

            const isLive =
              hasStream &&
              surface?.stream?.is_live !== false &&
              surface?.stream?.is_live !== 0;

            return (
              <button
                key={surface.id || surface.mat_number}
                type="button"
                onClick={() => onSelectMat?.(Number(surface.mat_number))}
                className={[
                  "group w-full rounded-2xl border p-3 text-left transition",
                  "focus:outline-none focus:ring-4 focus:ring-sky-500/15",

                  isSelected
                    ? "border-sky-600 bg-sky-600 text-white shadow-lg shadow-sky-600/20"
                    : "border-slate-200 bg-slate-50 text-slate-900 hover:border-sky-300 hover:bg-sky-50 dark:border-white/10 dark:bg-white/5 dark:text-white dark:hover:bg-sky-500/10",
                ].join(" ")}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={[
                      "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl",
                      isSelected
                        ? "bg-white/15 text-white"
                        : "bg-white text-sky-700 shadow-sm dark:bg-white/10 dark:text-sky-300",
                    ].join(" ")}
                  >
                    <SurfaceIcon />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-black">
                          {surface.label || `Tapis ${surface.mat_number}`}
                        </p>

                        <p
                          className={[
                            "mt-0.5 text-[11px] font-bold uppercase tracking-wider",
                            isSelected
                              ? "text-white/75"
                              : "text-slate-500 dark:text-slate-400",
                          ].join(" ")}
                        >
                          {config.label}
                        </p>
                      </div>

                      <span
                        className={[
                          "inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-[10px] font-black uppercase",

                          isSelected
                            ? "bg-white/15 text-white"
                            : open
                              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300"
                              : "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
                        ].join(" ")}
                      >
                        {open ? <FaCheckCircle /> : <FaTimesCircle />}

                        {open ? "Ouverte" : "Fermée"}
                      </span>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span
                        className={[
                          "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold",
                          isSelected
                            ? "bg-white/15 text-white"
                            : "bg-white text-slate-600 shadow-sm dark:bg-white/10 dark:text-slate-300",
                        ].join(" ")}
                      >
                        <FaListUl />
                        {activeSlots} catégorie
                        {activeSlots > 1 ? "s" : ""}
                      </span>

                      {hasStream ? (
                        <span
                          className={[
                            "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold",
                            isSelected
                              ? "bg-white/15 text-white"
                              : isLive
                                ? "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300"
                                : "bg-slate-200 text-slate-600 dark:bg-white/10 dark:text-slate-300",
                          ].join(" ")}
                        >
                          <FaBroadcastTower />

                          {isLive ? "Live actif" : "Live arrêté"}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>

      {selectedSurface ? (
        <div className="border-t border-slate-200 p-4 dark:border-white/10">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
            Surface sélectionnée
          </p>

          <p className="mt-2 truncate text-sm font-black text-slate-900 dark:text-white">
            {currentLabel || selectedSurface.label || `Tapis ${selectedMat}`}
          </p>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={onToggleCurrentMat}
              disabled={typeof onToggleCurrentMat !== "function"}
              className={[
                "inline-flex min-h-10 items-center justify-center gap-2 rounded-xl px-3 text-xs font-black transition",
                "disabled:cursor-not-allowed disabled:opacity-50",

                isOpen
                  ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:hover:bg-emerald-500/25"
                  : "bg-rose-100 text-rose-700 hover:bg-rose-200 dark:bg-rose-500/15 dark:text-rose-300 dark:hover:bg-rose-500/25",
              ].join(" ")}
            >
              {isOpen ? <FaUnlock /> : <FaLock />}

              {isOpen ? "Ouverte" : "Fermée"}
            </button>

            <button
              type="button"
              onClick={onDeleteSurface}
              disabled={
                deletingSurface || typeof onDeleteSurface !== "function"
              }
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-rose-600 px-3 text-xs font-black text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
              title="Supprimer cette surface"
            >
              <FaTrash />

              {deletingSurface ? "Suppression…" : "Supprimer"}
            </button>
          </div>
        </div>
      ) : null}
    </aside>
  );
}
